"use client";

import type {MouseEvent} from "react";
import {DynamicIcon, type IconName} from "@repo/ui/aops-components/DynamicIcon";
import {Typography} from "@repo/ui/aops-components/typography";
import {cn} from "@repo/ui/lib/utils";
import type {Button as ButtonType} from "@repo/sanity-utils/schema/types";
import styles from "./DiscoveryButton.module.css";

export type DiscoveryButtonProps = {
	button: ButtonType & {_key?: string};
	className?: string;
	/** When true, opens the link in a new browser tab. */
	openInNewTab?: boolean;
};

// Check if the link is a same-page hash link
function isSamePageHashLink(href: string): boolean {
	return href.startsWith("#") && href.length > 1;
}

/**
 * Discovery page CTA button styled to PACT Summer Launch 2026 (Figma Button lg).
 * Renders a Sanity `button` as a link (or non-interactive span when disabled).
 * Same-page hash links (e.g. `#nextStep`) smooth-scroll to the target.
 */
export function DiscoveryButton({
	button,
	className,
	openInNewTab = false,
}: DiscoveryButtonProps) {
	const href = button.link && !button.disabled ? button.link : undefined;
	const iconName = button.icon;

	let variantClass = styles.default;
	if (button.variant === "link") {
		variantClass = styles.link;
	} else if (button.variant === "secondary") {
		variantClass = styles.secondary;
	}

	const icon = iconName ? (
		<DynamicIcon
			aria-hidden
			className={styles.icon}
			name={iconName as IconName}
			size={20}
			strokeWidth={2}
		/>
	) : undefined;

	const label = button.text ? (
		<Typography variant="buttonLg" as="span" className={styles.label}>
			{button.text}
		</Typography>
	) : (
		icon
	);

	const content =
		button.text && icon ? (
			<>
				{button.iconPosition === "left" && icon}
				{label}
				{(button.iconPosition === "right" || !button.iconPosition) && icon}
			</>
		) : (
			label
		);

	const classes = cn(
		styles.root,
		variantClass,
		button.disabled && styles.disabled,
		className
	);

	if (button.disabled) {
		return (
			<span className={classes} aria-disabled>
				{content}
			</span>
		);
	}

	// If the link is a same-page hash link, scroll to the target
	const onClick =
		href && isSamePageHashLink(href)
			? (event: MouseEvent<HTMLAnchorElement>) => {
					const target = document.getElementById(href.slice(1));
					if (!target) {
						return;
					}
					event.preventDefault();
					target.scrollIntoView({behavior: "smooth", block: "start"});
					window.history.replaceState(null, "", href);
				}
			: undefined;

	return (
		<a
			className={classes}
			href={href}
			onClick={onClick}
			{...(openInNewTab &&
				!onClick && {target: "_blank", rel: "noopener noreferrer"})}
		>
			{content}
		</a>
	);
}
